import { motion } from 'motion/react'
import type { ShopMessageKey } from '../../../i18n'
import { CustomerAreaSurfaceCard, EmptyStateBlock, customerMotionTransition } from '../customer-area'
import { LocationIcon } from './preferencesShared'

type AssociatedAddressSectionProps = {
  associatedAddress: string
  associatedName: string
  reducedMotion: boolean
  surfaceCardClassName: string
  onNavigate: (targetPath: string) => void
  toLocalizedPath: (path: string) => string
  t: (key: ShopMessageKey, params?: Record<string, string | number>) => string
}

export const AssociatedAddressSection = ({
  associatedAddress,
  associatedName,
  reducedMotion,
  surfaceCardClassName,
  onNavigate,
  toLocalizedPath,
  t,
}: AssociatedAddressSectionProps) => (
  <CustomerAreaSurfaceCard
    title={t('customer.preferences.address.title')}
    subtitle={t('customer.preferences.address.subtitle')}
    className={surfaceCardClassName}
    contentClassName="mt-4"
  >
    {associatedAddress ? (
      <motion.div
        whileHover={reducedMotion ? undefined : { y: -1, scale: 1.01 }}
        transition={customerMotionTransition}
        className="ca-card-shell ca-theme-transition rounded-xl px-4 py-3.5 hover:shadow-(--shadow-md)"
      >
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-(--color-border-default) bg-(--color-bg-surface-muted) text-(--color-text-secondary)">
            <LocationIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-wide text-(--color-text-muted)">
              {t('customer.preferences.address.summaryLabel')}
            </p>
            <p className="mt-1 text-sm font-semibold text-(--color-text-primary)">
              {associatedName || t('customer.preferences.address.nameFallback')}
            </p>
            <p className="mt-1 whitespace-pre-line text-sm text-(--color-text-secondary)">{associatedAddress}</p>
          </div>
        </div>
        <p className="mt-2 text-xs text-(--color-text-muted)">{t('customer.preferences.address.usageTitle')}</p>
        <ul className="mt-1 space-y-0.5 text-sm text-(--color-text-secondary)">
          <li>&bull; {t('customer.preferences.address.usageShipping')}</li>
          <li>&bull; {t('customer.preferences.address.usageBilling')}</li>
        </ul>
        <button
          type="button"
          onClick={() => onNavigate(toLocalizedPath('/dashboard/addresses'))}
          className="ca-btn-secondary inline-flex h-8 items-center px-3 text-sm font-medium"
        >
          {t('customer.preferences.address.manage')}
        </button>
      </motion.div>
    ) : (
      <EmptyStateBlock
        icon={<LocationIcon className="h-4 w-4" />}
        title={t('customer.preferences.address.emptyTitle')}
        description={t('customer.preferences.address.emptyDescription')}
        action={
          <button
            type="button"
            onClick={() => onNavigate(toLocalizedPath('/dashboard/addresses'))}
            className="ca-btn-secondary inline-flex h-9 items-center px-3 text-sm font-medium"
          >
            {t('customer.preferences.address.emptyCta')}
          </button>
        }
      />
    )}
  </CustomerAreaSurfaceCard>
)
